import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initAnalytics, trackPage } from "@/lib/analytics";
initAnalytics();
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Create from "@/pages/Create";
import Library from "@/pages/Library";
import VideoDetail from "@/pages/VideoDetail";
import Series from "@/pages/Series";
import Schedule from "@/pages/Schedule";
import Credits from "@/pages/Credits";
import Referrals from "@/pages/Referrals";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import AcceptableUse from "@/pages/AcceptableUse";
import PricingPage from "@/pages/PricingPage";
import Contact from "@/pages/Contact";
import Guides from "@/pages/Guides";
import GuideArticle from "@/pages/GuideArticle";
import ErrorBoundary from "@/components/ErrorBoundary";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Faq from "@/pages/Faq";
import Compare from "@/pages/Compare";
import Comparison from "@/pages/Comparison";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="h-10 w-10 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPage(pathname);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  // Emergent Google Auth return: process #session_id BEFORE any route/guard runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/acceptable-use" element={<AcceptableUse />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/guides" element={<Guides />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/compare/:slug" element={<Comparison />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/guides/:slug" element={<GuideArticle />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/create" element={<Create />} />
        <Route path="/library" element={<Library />} />
        <Route path="/video/:id" element={<VideoDetail />} />
        <Route path="/series" element={<Series />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
